import { produce } from 'immer';
import _ from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HttpResponse } from '../../../../common/ajax';
import {
  getErrorMessage,
  getErrorState,
  hasValidationErrors,
  scrollToError,
  setValidationErrors,
  showCartToast,
  showErrorToast,
} from '../../../../common/global/helpers';
import useMount from '../../../../common/hooks/useMount';
import { ErrorPanel } from '../../../../common/widgets/ErrorPanel';
import LoadingSpinner from '../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../common/widgets/Notification';
import { config } from '../../config';
import { paymentMethodCodes } from '../../constants';
import { Budget } from '../../contexts/common';
import { CustomerBudgetContext } from '../../contexts/CustomerBudgetContext';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { PlatformInfoContext } from '../../contexts/PlatformInfoContext';
import { BudgetTypes, CheckOutWarningType, DyPageTypeEnum } from '../../enums';
import { CanCheckOutModel, CartSummaryModel } from '../../models/Cart';
import {
  CheckoutResponseModel,
  CheckOutState,
  InvoicingAddressModel,
} from '../../models/Checkout';
import {
  ErrorState,
  ErrorType,
  ValidationErrorModel,
} from '../../models/Errors';
import { resources } from '../../resources/resources';
import { cartService } from '../../services/cart';
import { checkoutService } from '../../services/checkout';
import { InvoicingAddress } from '../Common/Dialogs/InvoicingAddress/types';
import { LimitErrorDetailsDialog } from '../Common/Dialogs/LimitErrorDetailsDialog';
import { NoPaymentMethodsDialog } from '../Common/Dialogs/NoPaymentMethodsDialog';
import { EmptyStateEmptyCart } from '../Common/EmptyStates';
import RecommendedProducts from '../Common/RecommendedProducts';
import {
  cashClubMethodCode,
  freeCheckoutMethodCode,
  QuantityChangeType,
} from './CartPageCommon';
import { CartPaymentMethods } from './Components/CartPaymentMethods';
import { CartProduct } from './Components/CartProduct';
import { CartSummary } from './Components/CartSummary';
import { InvoicingAddressForm } from './Components/InvoicingAddress/InvoicingAddressForm';
import WarningsComponent from './Components/WarningsComponent';

export default function CartPage() {
  const activeItemIdRef = useRef<number | null>(null);
  const [isCheckoutButtonLoading, setIsCheckoutButtonLoading] =
    useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const isDeletingRef = useRef(false);
  const isUndoDeletingRef = useRef(false);
  const isEditingQuantityRef = useRef(false);
  const sessionIdRef = useRef(globalThis.crypto.randomUUID());

  const [cartSummary, setCartSummary] = useState<CartSummaryModel | null>(null);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [useBenefitBudgetList, setUseBenefitBudgetList] = useState<number[]>(
    [],
  );
  const [selectedCheckoutMethod, setSelectedCheckoutMethod] =
    useState<string>();
  const [errors, setErrors] = useState<ErrorState>({});
  const [showLimitErrorDialog, setShowLimitErrorDialog] =
    useState<boolean>(false);
  const [showNoPaymentMethodsDialog, setShowNoPaymentMethodsDialog] =
    useState<boolean>(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const [areQuantitySelectorsDisabled, setAreQuantitySelectorsDisabled] =
    useState<boolean>(false);
  const [invoicingAddress, setInvoicingAddress] =
    useState<InvoicingAddress | null>(null);
  const [updateCustomerInvoicingAddress, setUpdateCustomerInvoicingAddress] =
    useState<boolean>(false);

  const { t: translate } = useTranslation();
  const platformInfo = useContext(PlatformInfoContext);

  const { customerInfo, setCustomerInfo, loadCustomerInfo } =
    useContext(CustomerInfoContext);
  const { customerBudget, loadCustomerBalance, updateCustomerBudgetCashback } =
    useContext(CustomerBudgetContext);

  const navigate = useNavigate();

  const invoicingAddressNeeded = useMemo(() => {
    if (!selectedCheckoutMethod) {
      return false;
    }
    return [
      paymentMethodCodes.mobilpay,
      paymentMethodCodes.mobilpaycv,
      paymentMethodCodes.mobilpaygv,
      paymentMethodCodes.mobilpaytv,
      paymentMethodCodes.cashclub,
    ].includes(selectedCheckoutMethod);
  }, [selectedCheckoutMethod]);

  const updateCartItemAbortController = (
    cartItemId: number,
    abortController?: AbortController,
  ) => {
    setCartSummary(
      produce(draft => {
        if (!draft) return;
        const cartItem = draft.products.find(
          it => it.cartItemId === cartItemId,
        );
        if (cartItem) cartItem.abortController = abortController;
      }),
    );
  };

  const handleCartSummaryResponse = (summary: CartSummaryModel) => {
    setCartSummary(summary);

    // Set useBenefitBudgetList
    const useBenefitBudgetProducts = summary.products
      .filter(product => product.useBenefitBudget)
      .map(product => product.cartItemId);
    setUseBenefitBudgetList(useBenefitBudgetProducts);

    if (summary.customerInvoicingAddress) {
      setInvoicingAddress({
        ...summary.customerInvoicingAddress,
        agreeCnp: !!summary.customerInvoicingAddress.cnp,
      });
    } else {
      setUpdateCustomerInvoicingAddress(true);
    }
    setErrors(getErrorState(summary.validationErrors));

    const isCardChecked = summary.selectedCheckoutMethods.some(
      item =>
        item === BudgetTypes.BenefitAndCoPayment ||
        item === BudgetTypes.CoPaymentOnly,
    );

    const isFreeCheckout =
      summary.selectedCheckoutMethods.includes(BudgetTypes.BenefitBudgetOnly) &&
      (summary.paidFromBudget > 0 ||
        (summary.paidFromContribution === 0 &&
          !summary.cashbackDiscount &&
          summary.products.some(
            product => product.isBenefitInKind && product.canUseBenefitBudget,
          )));
    const isCashback =
      summary.paidFromBudget === 0 &&
      summary.paidFromContribution === 0 &&
      !!summary.cashbackDiscount;
    const isSalaryDeductionChecked = summary.isSalaryDeductionSelected;

    if (isSalaryDeductionChecked) {
      setSelectedCheckoutMethod(paymentMethodCodes.salaryDeduction);
    } else if (isCardChecked) {
      setSelectedCheckoutMethod(paymentMethodCodes.mobilpay);
    } else if (isFreeCheckout) {
      setSelectedCheckoutMethod(freeCheckoutMethodCode);
    } else if (isCashback) {
      setSelectedCheckoutMethod(cashClubMethodCode);
    } else {
      setSelectedCheckoutMethod(undefined);
    }

    if (summary?.products?.length !== customerInfo?.cartItemsCount) {
      loadCartItems();
    }
  };

  const loadCart = useCallback(
    async (abortController?: AbortController) => {
      setIsSummaryLoading(true);
      setAreQuantitySelectorsDisabled(true);
      const r = await cartService.getCart({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      setIsSummaryLoading(false);
      setAreQuantitySelectorsDisabled(false);

      if (!r.hasErrors) {
        handleCartSummaryResponse(r.data);
      } else {
        if (r.errorType === ErrorType.ValidationError) {
          setValidationErrors(r, setErrors);
        } else {
          setShowErrorPanel(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useMount(abortController => {
    // Load cart with empty list initially, server will return the correct useBenefitBudgetList
    loadCart(abortController);
  });
  const customerInfoRef = useRef(customerInfo);
  const setCustomerInfoRef = useRef(setCustomerInfo);

  useEffect(() => {
    customerInfoRef.current = customerInfo;
    setCustomerInfoRef.current = setCustomerInfo;
  }, [customerInfo, setCustomerInfo]);

  const loadCartItems = useCallback(async () => {
    try {
      const r = await cartService.getCartItems();

      if (!r.hasErrors) {
        const updatedCustomerInfo = produce(customerInfoRef.current, draft => {
          draft.cartItemsCount = r.data.length;
        });
        setCustomerInfoRef.current(updatedCustomerInfo);
      } else {
        setShowErrorPanel(true);
      }
    } catch (error) {
      setShowErrorPanel(true);
    }
  }, [customerInfoRef, setCustomerInfoRef, setShowErrorPanel]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // The page was restored from bfcache
      if (event.persisted) {
        loadCartItems();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [loadCartItems]);

  const undoDeleteCartItem = async (cartItemId: number) => {
    const cartItem = cartSummary?.products.find(
      e => e.cartItemId === cartItemId,
    );
    if (!cartItem) return;
    if (isUndoDeletingRef.current) return;
    isUndoDeletingRef.current = true;

    setIsSummaryLoading(true);
    setAreQuantitySelectorsDisabled(true);
    try {
      const r = await cartService.undoRemoveProduct(
        cartItem.productId,
        cartItem.quantity,
        cartItem.options,
      );
      setIsSummaryLoading(false);
      setAreQuantitySelectorsDisabled(false);

      if (r.hasErrors) {
        setShowErrorPanel(true);
      } else {
        handleCartSummaryResponse(r.data);
        setCustomerInfo(
          produce(customerInfo, draft => {
            draft.cartItemsCount = r.data.products.length;
          }),
        );
      }
    } finally {
      isUndoDeletingRef.current = false;
    }
  };

  const deleteCartItem = async (cartItemId: number) => {
    if (isDeletingRef.current) return;
    isDeletingRef.current = true;

    setIsSummaryLoading(true);
    setAreQuantitySelectorsDisabled(true);
    try {
      const r = await cartService.removeProduct(cartItemId);
      setIsSummaryLoading(false);
      setAreQuantitySelectorsDisabled(false);

      if (!r.hasErrors) {
        const newUseBenefitBudgetList = useBenefitBudgetList.filter(
          ci => ci !== cartItemId,
        );
        setUseBenefitBudgetList(newUseBenefitBudgetList);
        handleCartSummaryResponse(r.data);
        setCustomerInfo(
          produce(customerInfo, draft => {
            draft.cartItemsCount = r.data.products.length;
          }),
        );
        showCartToast(
          translate(resources.products.actions.productRemovedFromCart),
          false,
          () => undoDeleteCartItem(cartItemId),
        );
        const cartItem = cartSummary?.products.find(
          e => e.cartItemId === cartItemId,
        );
        if (cartItem && customerBudget.benefitBucksBalance) {
          updateCustomerBudgetCashback(
            customerBudget.benefitBucksBalance + cartItem.cashbackDiscount,
          );
        }
      } else {
        if (r.errorType === ErrorType.ValidationError) {
          setValidationErrors(r, setErrors);
        } else {
          setShowErrorPanel(true);
        }
      }
    } finally {
      isDeletingRef.current = false;
    }
  };

  const toggleUseBenefitBudgetCartItem = useCallback(
    async (cartItemId: number) => {
      setIsSummaryLoading(true);
      setAreQuantitySelectorsDisabled(true);
      const r = await cartService.toggleUseBenefitBudgetProduct(cartItemId);
      setIsSummaryLoading(false);
      setAreQuantitySelectorsDisabled(false);

      if (r.hasErrors) {
        if (r.errorType === ErrorType.ValidationError) {
          setValidationErrors(r, setErrors);
        } else {
          setShowErrorPanel(true);
        }
      } else {
        handleCartSummaryResponse(r.data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getInvoicingAddressToSend = (): InvoicingAddressModel | null => {
    if (!invoicingAddressNeeded || !invoicingAddress) return null;

    return {
      cityId: invoicingAddress.city.value,
      countyId: invoicingAddress.county.value,
      addressLine: invoicingAddress.addressLine,
      agreeCnp: invoicingAddress.agreeCnp,
      cnp: invoicingAddress.cnp ?? null,
      updateCustomerInvoicingAddress,
    };
  };

  const handleCheckoutError = (
    response: HttpResponse<CheckoutResponseModel>,
  ) => {
    if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
      scrollToError(response.error as ValidationErrorModel);
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const handleCheckout = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (!selectedCheckoutMethod) {
      isSubmittingRef.current = false;
      return;
    }

    const invoicingAddressToSend = getInvoicingAddressToSend();

    setIsCheckoutButtonLoading(true);
    let response;
    try {
      response = await checkoutService.checkout(
        sessionIdRef.current,
        selectedCheckoutMethod,
        invoicingAddressToSend,
      );
    } finally {
      setIsCheckoutButtonLoading(false);
      isSubmittingRef.current = false;
    }

    sessionIdRef.current = globalThis.crypto.randomUUID();
    if (response.hasErrors) {
      handleCheckoutError(response);
    } else {
      const state = response.data.state;
      const orderId = response.data.orderId;

      switch (state) {
        case CheckOutState.Duplicate:
          navigate(0);
          break;

        case CheckOutState.Placed:
          loadCustomerBalance();
          loadCustomerInfo();
          navigate(`/cart-success?orderId=${orderId}`);
          break;

        case CheckOutState.RedirectToPaymentProcessor:
          window.location.href = `${config.benefitApiUrl}/netopia/payment/${response.data.orderId}`;
          setCustomerInfo(
            produce(customerInfo, draft => {
              draft.cartItemsCount = 0;
            }),
          );
          break;
      }
    }
  };

  const handleQuantityChange = async (
    cartItemId: number,
    type: QuantityChangeType,
  ) => {
    const initialItem = cartSummary?.products.find(
      it => it.cartItemId === cartItemId,
    );
    if (!initialItem) return;

    const initialQuantity = initialItem.quantity;

    initialItem.abortController?.abort?.();

    const newQuantity =
      type === QuantityChangeType.Increase
        ? initialQuantity + 1
        : initialQuantity - 1;
    const abortController = new AbortController();

    setCartSummary(
      produce(d => {
        const cartItem = d!.products.find(it => it.cartItemId === cartItemId)!;
        cartItem.quantity = newQuantity;
        cartItem.abortController = abortController;
      }),
    );

    activeItemIdRef.current = initialItem.cartItemId;

    if (isEditingQuantityRef.current) return;
    isEditingQuantityRef.current = true;

    setIsSummaryLoading(true);
    try {
      const response = await cartService.editCartItem(cartItemId, newQuantity, {
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;
      setIsSummaryLoading(false);

      updateCartItemAbortController(cartItemId);
      activeItemIdRef.current = null;

      if (response.hasErrors) {
        if (response.errorType === ErrorType.ValidationError) {
          setValidationErrors(response, setErrors);
          const validationErrors = (response.error as ValidationErrorModel)
            .details;
          if (validationErrors.some(e => e.severity === 'error')) {
            setCartSummary(
              produce(d => {
                if (d) {
                  d.validationErrors = validationErrors;
                }
              }),
            );
          }
        } else {
          setShowErrorPanel(true);
        }
      } else {
        handleCartSummaryResponse(response.data);
      }
    } finally {
      isEditingQuantityRef.current = false;
    }
  };

  const getIsQuantitySelectorDisabled = (itemId: number) =>
    activeItemIdRef.current !== null && itemId !== activeItemIdRef.current;

  const canCheckout = (
    summary: CartSummaryModel,
    customerBudget: Budget,
  ): CanCheckOutModel => {
    if (isSummaryLoading || !summary)
      return { warningType: null, value: false };

    if (summary.validationErrors?.length > 0) {
      return { warningType: null, value: false };
    }

    const cardPaymentMethods = [
      paymentMethodCodes.mobilpay,
      paymentMethodCodes.mobilpaycv,
      paymentMethodCodes.mobilpaygv,
      paymentMethodCodes.mobilpaytv,
    ];

    if (
      !platformInfo?.isPlatformOpened &&
      summary.paidFromBudget > 0 &&
      !!selectedCheckoutMethod &&
      !cardPaymentMethods.includes(selectedCheckoutMethod)
    ) {
      return {
        warningType: CheckOutWarningType.PlatformIsClosed,
        value: false,
      };
    }

    if (summary.hasTemporaryDeactivatedProducts) {
      return {
        warningType: CheckOutWarningType.NoTemporaryDeactivated,
        value: false,
      };
    }

    if (
      summary.paidFromContribution > 0 &&
      (_.isNil(selectedCheckoutMethod) ||
        selectedCheckoutMethod === freeCheckoutMethodCode)
    ) {
      return {
        warningType: CheckOutWarningType.NoPaymentMethodSelected,
        value: false,
      };
    }

    if (
      summary.paidFromContribution >
        (customerBudget?.benefitBucksBalance ?? 0) &&
      selectedCheckoutMethod === cashClubMethodCode
    ) {
      return {
        warningType: CheckOutWarningType.NotEnoughBenefitBucks,
        value: false,
      };
    }

    return { warningType: null, value: true };
  };

  if (showErrorPanel) return <ErrorPanel />;

  if (cartSummary == null) return <LoadingSpinner />;

  if (cartSummary.products.length === 0) {
    return (
      <section className="section-main section-full">
        <div className="container">
          <EmptyStateEmptyCart />
          <RecommendedProducts pageType={DyPageTypeEnum.Cart} />
        </div>
      </section>
    );
  }

  const updateCartItemIsFavorite = async (
    cartItemId: number,
    isFavorite: boolean,
  ) => {
    setCartSummary(
      produce(draft => {
        if (draft) {
          const cartItem = draft.products.find(
            it => it.cartItemId === cartItemId,
          );
          if (cartItem) {
            cartItem.isFavorite = isFavorite;
          }
        }
      }),
    );
  };

  return (
    <section className="section-main section-checkout checkout-page">
      {hasValidationErrors(errors, 'null') && (
        <div className="section-notification-wrapper">
          <Notification
            type={NotificationType.Error}
            messages={errors.null.map(e => ({
              message: getErrorMessage(e, translate),
              onShowDetailsClick: e.payload?.isAutobudgetLimitError
                ? () => setShowLimitErrorDialog(true)
                : undefined,
            }))}
          />
        </div>
      )}
      <div className="container">
        {/* Left side */}
        <div className="area-content">
          <h2>{translate(resources.cart.title.details)}</h2>
          <WarningsComponent
            canCheckout={canCheckout(cartSummary, customerBudget)}
          />
          {!cartSummary.paymentMethods.length && (
            <Notification
              type={NotificationType.Error}
              messages={[
                {
                  message: translate(resources.cart.errors.noPaymentMethods),
                  onShowDetailsClick: () => setShowNoPaymentMethodsDialog(true),
                  showDetailsTitle: translate(resources.cart.errors.howCanIPay),
                  showDetailsColor: 'var(--color-main-dark)',
                },
              ]}
            />
          )}
          <ul className="checkout-list list-reset">
            {cartSummary.products.map(ci => (
              <CartProduct
                key={ci.cartItemId}
                ci={ci}
                useBenefitBudgetList={useBenefitBudgetList}
                isCheckoutButtonLoading={isCheckoutButtonLoading}
                isSummaryLoading={isSummaryLoading}
                areQuantitySelectorsDisabled={areQuantitySelectorsDisabled}
                handleQuantityChange={handleQuantityChange}
                getIsQuantitySelectorDisabled={getIsQuantitySelectorDisabled}
                deleteCartItem={deleteCartItem}
                toggleUseBenefitBudgetCartItem={toggleUseBenefitBudgetCartItem}
                setUseBenefitBudgetList={setUseBenefitBudgetList}
                updateCartItemIsFavorite={updateCartItemIsFavorite}
              />
            ))}
          </ul>
          <CartPaymentMethods
            summary={cartSummary}
            isSummaryLoading={isSummaryLoading}
            selectedCheckoutMethod={selectedCheckoutMethod}
            setSelectedCheckoutMethod={setSelectedCheckoutMethod}
          />
          {invoicingAddressNeeded && (
            <InvoicingAddressForm
              invoicingAddress={invoicingAddress}
              setInvoicingAddress={setInvoicingAddress}
              customerInvoicingAddress={cartSummary.customerInvoicingAddress}
              setUpdateCustomerInvoicingAddress={
                setUpdateCustomerInvoicingAddress
              }
              errors={errors}
              setErrors={setErrors}
            />
          )}
        </div>

        <CartSummary
          summary={cartSummary}
          useBenefitBudgetList={useBenefitBudgetList}
          isCheckoutButtonLoading={isCheckoutButtonLoading}
          isSummaryLoading={isSummaryLoading}
          handleCheckout={handleCheckout}
          canCheckout={canCheckout}
        />

        <div className="checkout-recommended-products">
          <RecommendedProducts pageType={DyPageTypeEnum.Cart} />
        </div>
      </div>
      {showLimitErrorDialog && (
        <LimitErrorDetailsDialog
          closeDialog={() => setShowLimitErrorDialog(false)}
        />
      )}

      {showNoPaymentMethodsDialog && (
        <NoPaymentMethodsDialog
          closeDialog={() => setShowNoPaymentMethodsDialog(false)}
        />
      )}
    </section>
  );
}
